import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { collection, doc, getDoc, getDocs, getFirestore, query, updateDoc, where } from 'firebase/firestore'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Table from 'react-bootstrap/Table'
import swal from 'sweetalert'
import { app } from '../../firebase'

const normalizeText = (value) => String(value || '').trim()

const normalizeCourseName = (course = {}) => (
  course.mcourseName || course.courseName || course.name || course.title || 'Untitled course'
)

const normalizeTimestamp = (value) => {
  if (!value) return 0
  if (value.seconds) return value.seconds
  const parsed = new Date(value).getTime()
  return Number.isNaN(parsed) ? 0 : parsed
}

const normalizeAssignedCourses = (value) => {
  if (!Array.isArray(value)) return []

  return value
    .map((course, index) => {
      if (typeof course === 'string') {
        return { id: course, name: course }
      }

      return {
        id: course.id || course.courseId || course.value || `course-${index + 1}`,
        name: course.name || course.mcourseName || course.courseName || course.title || ''
      }
    })
    .filter((course) => course.name)
}

function InstructorProfileComp() {
  const db = getFirestore(app)
  const navigate = useNavigate()
  const { id: instructorId } = useParams()

  const [instructor, setInstructor] = useState(null)
  const [courses, setCourses] = useState([])
  const [assignedCourses, setAssignedCourses] = useState([])
  const [classes, setClasses] = useState([])
  const [selectedCourseId, setSelectedCourseId] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const loadInstructorProfile = async () => {
      if (!instructorId) return

      setLoading(true)
      try {
        const [instructorSnap, coursesSnap, classesSnap] = await Promise.all([
          getDoc(doc(db, 'Employees', instructorId)),
          getDocs(collection(db, 'courses')),
          getDocs(query(collection(db, 'allclasses'), where('instructor', '==', instructorId)))
        ])

        if (!instructorSnap.exists()) {
          setInstructor(null)
          return
        }

        const instructorData = { id: instructorSnap.id, ...instructorSnap.data() }
        const courseList = coursesSnap.docs
          .map((courseDoc) => {
            const data = courseDoc.data()
            return {
              id: courseDoc.id,
              name: normalizeCourseName(data),
              duration: data.mcourseDuration || data.courseDuration || '',
              raw: data
            }
          })
          .sort((first, second) => first.name.localeCompare(second.name))

        const classList = classesSnap.docs
          .map((classDoc) => ({ id: classDoc.id, ...classDoc.data() }))
          .sort((first, second) => normalizeTimestamp(second.timeCreated) - normalizeTimestamp(first.timeCreated))

        setInstructor(instructorData)
        setCourses(courseList)
        setAssignedCourses(normalizeAssignedCourses(
          instructorData.teachableCourses || instructorData.coursesCanTeach || instructorData.assignedCourses
        ))
        setClasses(classList)
      } catch (error) {
        console.error('Error loading instructor profile:', error)
        swal('Error', 'Could not load instructor details.', 'error')
      } finally {
        setLoading(false)
      }
    }

    loadInstructorProfile()
  }, [db, instructorId])

  const availableCourses = useMemo(() => {
    const assignedIds = new Set(assignedCourses.map((course) => course.id))
    const assignedNames = new Set(assignedCourses.map((course) => course.name.toLowerCase()))

    return courses.filter((course) => (
      !assignedIds.has(course.id) && !assignedNames.has(course.name.toLowerCase())
    ))
  }, [assignedCourses, courses])

  const activeClasses = useMemo(() => {
    return classes.filter((classItem) => String(classItem.isActive || '').trim().toLowerCase() === 'active').length
  }, [classes])

  const addCourse = () => {
    if (!selectedCourseId) return

    const course = courses.find((item) => item.id === selectedCourseId)
    if (!course) return

    setAssignedCourses((current) => [
      ...current,
      { id: course.id, name: course.name }
    ])
    setSelectedCourseId('')
  }

  const removeCourse = (courseId) => {
    setAssignedCourses((current) => current.filter((course) => course.id !== courseId))
  }

  const saveCourses = async () => {
    if (!instructorId) return

    setSaving(true)
    try {
      await updateDoc(doc(db, 'Employees', instructorId), {
        teachableCourses: assignedCourses,
        updatedAt: new Date().toISOString()
      })
      setInstructor((current) => ({ ...current, teachableCourses: assignedCourses }))
      swal('Saved', 'Instructor course assignments updated.', 'success')
    } catch (error) {
      console.error('Error saving instructor courses:', error)
      swal('Error', 'Could not save course assignments.', 'error')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="instructorViewPage">
        <div className="instructorViewState">
          <div className="spinner-border text-dark" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <span>Loading instructor profile...</span>
        </div>
      </div>
    )
  }

  if (!instructor) {
    return (
      <div className="instructorViewPage">
        <div className="instructorViewState">
          <h5>Instructor not found</h5>
          <p>This instructor record could not be found in Employees.</p>
          <button type="button" className="instructorViewGhostBtn" onClick={() => navigate('/instructors')}>
            Back to instructors
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="instructorViewPage">
      <section className="instructorViewHeader">
        <div className="instructorViewHeaderTop">
          <button type="button" className="instructorViewTextBtn" onClick={() => navigate('/instructors')}>
            Back to instructors
          </button>
          <button type="button" className="instructorViewGhostBtn" onClick={() => navigate(`/view-employee/${instructor.id}`)}>
            Open HR profile
          </button>
        </div>

        <div className="instructorViewIdentity">
          <div className="instructorViewAvatar">
            {(instructor.employeeName || 'I').trim().charAt(0).toUpperCase()}
          </div>
          <div className="instructorViewIdentityCopy">
            <p className="instructorViewEyebrow">Instructor profile</p>
            <h3>{instructor.employeeName || 'Instructor'}</h3>
            <p>Manage teaching coverage and review the classes assigned from Darasa.</p>
            <div className="instructorViewMeta">
              <span>{instructor.employeeEmail || instructor.currentEmail || 'Email not set'}</span>
              <span>{instructor.employeePhone || 'Phone not set'}</span>
              <span>{instructor.campus || 'Campus not set'}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="instructorViewStats">
        <article className="instructorViewStatCard">
          <span>Can teach</span>
          <strong>{assignedCourses.length}</strong>
        </article>
        <article className="instructorViewStatCard">
          <span>Assigned classes</span>
          <strong>{classes.length}</strong>
        </article>
        <article className="instructorViewStatCard">
          <span>Active classes</span>
          <strong>{activeClasses}</strong>
        </article>
        <article className="instructorViewStatCard">
          <span>Role</span>
          <strong>{instructor.jobPosition || instructor.position || instructor.userType || 'Instructor'}</strong>
        </article>
      </section>

      <section className="instructorViewGrid">
        <article className="instructorViewPanel instructorViewPanelWide">
          <div className="instructorViewPanelHeader">
            <div>
              <span className="instructorViewEyebrow">Course coverage</span>
              <h5>Courses this instructor can teach</h5>
            </div>
          </div>

          <div className="instructorCoursePicker">
            <Form.Select value={selectedCourseId} onChange={(event) => setSelectedCourseId(event.target.value)}>
              <option value="">Select a course to assign</option>
              {availableCourses.map((course) => (
                <option key={course.id} value={course.id}>{course.name}</option>
              ))}
            </Form.Select>
            <Button variant="dark" onClick={addCourse} disabled={!selectedCourseId}>
              Add course
            </Button>
          </div>

          <div className="instructorCourseChips">
            {assignedCourses.length > 0 ? assignedCourses.map((course) => (
              <span key={course.id} className="instructorCourseChip">
                {course.name}
                <button type="button" onClick={() => removeCourse(course.id)}>Remove</button>
              </span>
            )) : (
              <p className="instructorViewMuted mb-0">No teachable courses assigned yet.</p>
            )}
          </div>

          <div className="instructorProfileActions">
            <Button variant="dark" onClick={saveCourses} disabled={saving}>
              {saving ? 'Saving...' : 'Save course assignments'}
            </Button>
          </div>
        </article>

        <aside className="instructorViewPanel">
          <div className="instructorViewPanelHeader">
            <div>
              <span className="instructorViewEyebrow">Darasa link</span>
              <h5>How classes are matched</h5>
            </div>
          </div>
          <p className="instructorViewMuted">
            Darasa stores classes in <strong>allclasses</strong>. This page shows classes where
            the class <strong>instructor</strong> field equals this employee document id.
          </p>
          <div className="instructorViewInfoGrid">
            <div className="instructorViewInfoCard">
              <span>Employee ID</span>
              <strong>{instructor.id}</strong>
            </div>
            <div className="instructorViewInfoCard">
              <span>Status</span>
              <strong>{instructor.employeeStatus || 'Active'}</strong>
            </div>
          </div>
        </aside>
      </section>

      <section className="instructorViewPanel instructorClassesPanel">
        <div className="instructorViewPanelHeader">
          <div>
            <span className="instructorViewEyebrow">Classes</span>
            <h5>Classes assigned to this instructor</h5>
          </div>
          <span className="employeeStatusBadge active">{classes.length} class{classes.length === 1 ? '' : 'es'}</span>
        </div>

        <div className="appsTableWrap">
          <Table hover responsive className="appsTable">
            <thead>
              <tr>
                <th>Class</th>
                <th>Status</th>
                <th>Intake</th>
                <th>Start date</th>
                <th>Time</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {classes.length === 0 ? (
                <tr>
                  <td colSpan="6" className="appsEmptyState">
                    No Darasa classes are currently assigned to this instructor.
                  </td>
                </tr>
              ) : classes.map((classItem) => {
                const roomId = classItem.roomID || classItem.id
                return (
                  <tr key={classItem.id}>
                    <td>{classItem.classDesc || 'Class'}</td>
                    <td>{classItem.isActive || 'Status not set'}</td>
                    <td>{classItem.intakeDate || 'Not set'}</td>
                    <td>{classItem.startDate || 'Not set'}</td>
                    <td>{normalizeText(classItem.startTime) || '—'} - {normalizeText(classItem.endTime) || '—'}</td>
                    <td>
                      <Button className="instructorViewTableBtn" variant="outline-dark" size="sm" onClick={() => navigate(`/view-classes?${roomId}`)}>
                        View class
                      </Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </div>
      </section>
    </div>
  )
}

export default InstructorProfileComp
